'use client';

/**
 * OfflineGameContext.js — Kontekst gry offline
 *
 * Uruchamia silnik gry bezpośrednio w głównym wątku (bez Web Worker).
 * Web Worker wymagałby ładowania chunków Next.js, co nie działa gdy
 * przeglądarka jest w trybie offline (blokuje localhost).
 *
 * Gra jest zapisywana w IndexedDB po każdej turze.
 * Wyniki są synchronizowane z serwerem po odzyskaniu połączenia.
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import * as offlineDb from '@/lib/offlineDb';
import { setupOnlineSyncListener } from '@/lib/syncService';
// Static imports — bundled into the main chunk so they work offline without chunk fetching
import GameEngine from '@/lib/offline/GameEngine';
import TurnManager from '@/lib/offline/TurnManager';
import BotManager from '@/lib/offline/BotManager';
import CardEffects from '@/lib/offline/CardEffects';
import { createGameState, createPlayerState } from '@/lib/offline/gameStateHelpers';

const OfflineGameContext = createContext(null);

// Engine is already imported statically above
function getEngine() {
  return { GameEngine, TurnManager, BotManager, CardEffects, createGameState, createPlayerState };
}

export function OfflineGameProvider({ children }) {
  const [gameState, setGameState] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOfflineGame] = useState(true);

  // Live game object — contains methods, kept in ref to avoid stale closures
  const liveGameRef = useRef(null);

  // Setup sync listener on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cleanup = setupOnlineSyncListener(() => {
      return localStorage.getItem('token') || null;
    });
    return cleanup;
  }, []);

  /**
   * Publish game state to React (strip functions for serialization) + save to IndexedDB
   */
  const publishState = useCallback(async (game, saveToDb = true) => {
    if (!game) return;

    // Shallow snapshot for React state (functions will be stripped by JSON)
    const snapshot = JSON.parse(JSON.stringify(game, (key, value) => {
      if (typeof value === 'function') return undefined;
      return value;
    }));

    setGameState(snapshot);

    if (saveToDb && game.gameId) {
      try {
        await offlineDb.saveGame({ ...snapshot, gameId: game.gameId });
      } catch (e) {
        console.warn('[OfflineGame] Failed to save to IndexedDB:', e);
      }
    }
  }, []);

  /**
   * Utwórz nową grę offline
   */
  const createOfflineGame = useCallback(async ({ playerName, botCount = 1, difficulty = 'medium', userId = 'local-player' }) => {
    setIsLoading(true);
    setError(null);
    try {
      const { GameEngine, TurnManager, createGameState, createPlayerState } = getEngine();

      const humanPlayer = createPlayerState({ userId, username: playerName, isBot: false });
      const bots = Array.from({ length: botCount }, (_, i) =>
        createPlayerState({ userId: `bot-${i + 1}`, username: `Bot ${i + 1}`, isBot: true, difficulty })
      );

      const game = createGameState({ code: 'OFFLINE', players: [humanPlayer, ...bots] });
      await GameEngine.initializeGame(game);

      const firstPlayer = game.getCurrentPlayer();
      await TurnManager.startPhase(game, firstPlayer);

      liveGameRef.current = game;
      await publishState(game);
      setGameId(game.gameId);
      return game;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [publishState]);

  /**
   * Załaduj istniejącą grę offline z IndexedDB
   */
  const loadOfflineGame = useCallback(async (gId) => {
    setIsLoading(true);
    try {
      const { createGameState, createPlayerState } = getEngine();
      const saved = await offlineDb.loadGame(gId);
      if (!saved) throw new Error('Gra nie znaleziona w pamięci lokalnej');

      // Restore live object with methods from saved plain JSON
      const game = restoreLiveGame(saved, createGameState, createPlayerState);
      liveGameRef.current = game;
      await publishState(game, false);
      setGameId(gId);
      return game;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [publishState]);

  /**
   * Zagraj kartę
   */
  const playCard = useCallback(async (cardId, playerId) => {
    const game = liveGameRef.current;
    if (!game) return;
    try {
      const { GameEngine } = getEngine();
      const player = game.players.find(p => p.userId === playerId);
      if (!player) throw new Error('Player not found');
      await GameEngine.playCard(game, player, cardId);
      await publishState(game);
    } catch (e) {
      setError(e.message);
    }
  }, [publishState]);

  /**
   * Zakończ turę i uruchom tury botów
   */
  const endTurn = useCallback(async (playerId) => {
    const game = liveGameRef.current;
    if (!game) return;
    try {
      setIsLoading(true);
      const { TurnManager, BotManager } = getEngine();

      const player = game.players.find(p => p.userId === playerId);
      if (!player) throw new Error('Player not found');

      // End human's turn
      await TurnManager.endPhaseWithoutRefill(game, player);
      if (game.refillAllZones) game.refillAllZones();
      TurnManager.nextPlayer(game);

      // Check win
      if (game.checkGameEnd && game.checkGameEnd()) {
        game.calculateWinner();
        await publishState(game);
        await handleGameEnd(game);
        return;
      }

      await publishState(game);

      // Run all consecutive bot turns
      let currentPlayer = game.getCurrentPlayer();
      while (currentPlayer?.isBot && !game.winner && game.status !== 'finished') {
        await BotManager.executeBotTurn(game, currentPlayer);
        if (game.refillAllZones) game.refillAllZones();
        TurnManager.nextPlayer(game);

        if (game.checkGameEnd && game.checkGameEnd()) {
          game.calculateWinner();
          await publishState(game);
          await handleGameEnd(game);
          return;
        }

        await publishState(game);
        currentPlayer = game.getCurrentPlayer();
      }

      // Start human's new turn
      if (!game.winner && game.status !== 'finished') {
        const nextHuman = game.getCurrentPlayer();
        if (nextHuman && !nextHuman.isBot) {
          await TurnManager.startPhase(game, nextHuman);
          await publishState(game);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [publishState]);

  /**
   * Kup kartę
   */
  const purchaseCard = useCallback(async (cardId, playerId) => {
    const game = liveGameRef.current;
    if (!game) return;
    try {
      const { GameEngine } = getEngine();
      const player = game.players.find(p => p.userId === playerId);
      if (!player) throw new Error('Player not found');
      await GameEngine.purchaseCard(game, player, cardId);
      await publishState(game);
    } catch (e) {
      setError(e.message);
    }
  }, [publishState]);

  /**
   * Wyzwij potwora
   */
  const challengeMonster = useCallback(async (pathType, cardIndex, playerId, options = {}) => {
    const game = liveGameRef.current;
    if (!game) return;
    try {
      const { GameEngine } = getEngine();
      const player = game.players.find(p => p.userId === playerId);
      if (!player) throw new Error('Player not found');
      await GameEngine.challengePath(game, player, pathType || 'courage', cardIndex ?? 0, {
        paySurcharge: options.paySurcharge === true
      });
      await publishState(game);
    } catch (e) {
      setError(e.message);
    }
  }, [publishState]);

  /**
   * Rozwiąż oczekujący efekt
   */
  const resolveEffect = useCallback(async (effectId, choice, cardId, playerId) => {
    const game = liveGameRef.current;
    if (!game) return;
    try {
      const { CardEffects } = getEngine();
      const player = game.players.find(p => p.userId === playerId);
      if (!player) throw new Error('Player not found');

      const effectIndex = effectId
        ? player.pendingEffects.findIndex(e => e.id === effectId || e.type === effectId)
        : 0;
      if (effectIndex === -1) throw new Error('Pending effect not found');

      const pendingEffect = player.pendingEffects[effectIndex];
      const context = cardId ? { cardId, choice } : { choice };

      await CardEffects.applyEffect(game, player, pendingEffect, context);
      player.pendingEffects.splice(effectIndex, 1);

      await publishState(game);
    } catch (e) {
      setError(e.message);
    }
  }, [publishState]);

  /**
   * Koniec gry — zapisz wynik do sync queue
   */
  const handleGameEnd = useCallback(async (game) => {
    const humanPlayer = game.players.find(p => !p.isBot);
    if (!humanPlayer) return;

    const result = {
      isWinner: game.winner === humanPlayer.userId,
      playerFame: humanPlayer.fame || 0,
      turns: game.currentTurn,
      difficulty: game.players.find(p => p.isBot)?.difficulty || 'medium',
    };

    if (game.gameId) {
      await offlineDb.markGameFinished(game.gameId, result);
    }
  }, []);

  return (
    <OfflineGameContext.Provider
      value={{
        gameState,
        gameId,
        isLoading,
        error,
        isOfflineGame,
        createOfflineGame,
        loadOfflineGame,
        playCard,
        endTurn,
        purchaseCard,
        challengeMonster,
        resolveEffect,
        clearError: () => setError(null),
      }}
    >
      {children}
    </OfflineGameContext.Provider>
  );
}

export function useOfflineGame() {
  const ctx = useContext(OfflineGameContext);
  if (!ctx) throw new Error('useOfflineGame must be used within OfflineGameProvider');
  return ctx;
}

/**
 * Przywróć żywy obiekt gry z płaskiego JSON (po wczytaniu z IndexedDB)
 * Re-przyłącza wszystkie metody które JSON.stringify usunął
 */
function restoreLiveGame(plain, createGameState, createPlayerState) {
  // Re-attach player methods
  const players = (plain.players || []).map(p => {
    const player = { ...p };
    player.drawCards = function(count) {
      const drawn = [];
      for (let i = 0; i < count; i++) {
        if (this.deck.length === 0) {
          if (this.discard.length === 0) break;
          this.deck = this.discard.sort(() => Math.random() - 0.5);
          this.discard = [];
        }
        const card = this.deck.shift();
        if (card) { this.hand.push(card); drawn.push(card); }
      }
      return drawn;
    };
    player.discardPlayed = function() { this.discard.push(...this.played); this.played = []; };
    player.discardHand = function() { this.discard.push(...this.hand); this.hand = []; };
    player.resetTurnCounters = function(gameConfig) {
      this.mainActionsRemaining = gameConfig?.mainActionsPerTurn || 2;
      this.purchasesRemaining = 1 + (this.extraPurchases || 0);
      this.challengesRemaining = 1 + (this.extraChallenges || 0);
      this.extraPurchases = 0;
      this.extraChallenges = 0;
      this.bonusActionsThisTurn = 0;
      this.artifactsUsedThisTurn = [];
      this.cityZonesUsedThisTurn = {};
      this.actionCount = 0;
      this.purchasedThisTurn = false;
    };
    player.resetHeroAbility = function() { this.heroAbilityUsedThisTurn = false; };
    player.clearExpiredEffects = function(currentTurn) {
      this.tempEffects = (this.tempEffects || []).filter(
        e => !e.expiresAfterTurn || e.expiresAfterTurn > currentTurn
      );
    };
    player.shuffleArray = function(arr) { return arr.sort(() => Math.random() - 0.5); };
    return player;
  });

  const game = { ...plain, players };

  // Re-attach game methods
  game.getCurrentPlayer = function() { return this.players[this.currentPlayerIndex]; };
  game.nextPlayer = function() {
    this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;
    if (this.currentPlayerIndex === 0) this.currentTurn++;
  };
  game.logEvent = function(userId, username, type, data) {
    this.eventLog = this.eventLog || [];
    this.eventLog.push({ userId, username, type, data, timestamp: Date.now() });
  };
  game.markModified = function() {};
  game.refillAllZones = function() {
    while (this.marketplace.length < (this.gameConfig?.marketplaceSize || 6) && this.marketDeck.length > 0)
      this.marketplace.push(this.marketDeck.shift());
    while (this.pathOfCourage.length < (this.gameConfig?.pathSize || 4) && this.courageDeck.length > 0)
      this.pathOfCourage.push(this.courageDeck.shift());
    while (this.pathOfWisdom.length < (this.gameConfig?.pathSize || 4) && this.wisdomDeck.length > 0)
      this.pathOfWisdom.push(this.wisdomDeck.shift());
  };
  game.checkGameEnd = function() {
    return this.marketDeck.length === 0 && this.courageDeck.length === 0 &&
      this.wisdomDeck.length === 0 && this.marketplace.length === 0 &&
      this.pathOfCourage.length === 0 && this.pathOfWisdom.length === 0;
  };
  game.calculateWinner = function() {
    if (!this.players.length) return;
    const sorted = [...this.players].sort((a, b) => (b.fame || 0) - (a.fame || 0));
    this.winner = sorted[0].userId;
    this.status = 'finished';
    this.finishedAt = Date.now();
  };
  game.revealGuildContracts = function(count = 3) {
    const toReveal = count - (this.revealedGuildContracts?.length || 0);
    for (let i = 0; i < toReveal && this.guildContractDeck?.length > 0; i++)
      this.revealedGuildContracts.push(this.guildContractDeck.shift());
  };
  game.clearExpiredDurationEffects = function() { return []; };

  return game;
}
